/**
 * Database CRUD operations for Supabase tables
 * This file contains helper functions for all database operations
 */

import { supabase } from './supabase';
import { compressImage } from '../utils/imageCompression';
import { encryptFile, isEncryptionEnabled } from '../utils/documentEncryption';

const fetchAllPages = async (buildQuery, options = {}) => {
    const pageSize = Math.max(1, Math.min(Number(options.pageSize) || 1000, 10000));
    let from = 0;
    const all = [];

    // Some PostgREST setups enforce a max rows per request (often 100).
    // Paging with range() reliably retrieves everything.
    // eslint-disable-next-line no-constant-condition
    while (true) {
        const to = from + pageSize - 1;
        const { data, error } = await buildQuery().range(from, to);
        if (error) throw error;
        const rows = data || [];
        all.push(...rows);
        if (rows.length < pageSize) break;
        from += pageSize;
    }

    return all;
};

const getPageRange = (page = 1, pageSize = 25) => {
    const safePage = Math.max(1, Number(page) || 1);
    const safePageSize = Math.max(1, Math.min(Number(pageSize) || 25, 1000));
    const from = (safePage - 1) * safePageSize;
    const to = from + safePageSize - 1;
    return { from, to, safePage, safePageSize };
};

// Approvals search runs on the customers table (name/CIN), then filters work rows by customer_id.
// Filtering joined customers.* in PostgREST .or() is unreliable for paged approvals queries.
const getApprovalsSearchCustomerIds = async (filters = {}) => {
    const term = String(filters.search || '').trim();
    if (!term) return [];

    const buildQuery = () => {
        let query = supabase.from('customers').select('id');
        if (filters.project_id) query = query.eq('project_id', filters.project_id);
        if (filters.cluster_id) query = query.eq('cluster_id', filters.cluster_id);
        if (filters.region_id?.length) {
            query = Array.isArray(filters.region_id)
                ? query.in('region_id', filters.region_id)
                : query.eq('region_id', filters.region_id);
        }
        if (filters.team_id) {
            if (filters.team_id === 'not_assigned') query = query.is('team_id', null);
            else query = query.eq('team_id', filters.team_id);
        }
        return query.or(`name.ilike.%${term}%,cin.ilike.%${term}%`);
    };

    const rows = await fetchAllPages(buildQuery, { pageSize: 1000 });
    return rows.map((r) => r.id).filter(Boolean);
};

// Returns null = no customer_id restriction; [] = no matches.
const resolveApprovalsPagedCustomerIds = async (filters = {}) => {
    const hasSearch = Boolean(String(filters.search || '').trim());
    const hasAssignmentIds =
        Array.isArray(filters.customer_ids) && filters.customer_ids.length > 0;

    if (!hasSearch && !hasAssignmentIds) return null;

    let ids = hasAssignmentIds ? [...filters.customer_ids] : null;

    if (hasSearch) {
        const searchIds = await getApprovalsSearchCustomerIds(filters);
        if (!searchIds.length) return [];
        ids = ids ? ids.filter((id) => searchIds.includes(id)) : searchIds;
    }

    return ids?.length ? ids : [];
};

const applyApprovalsPagedCustomerFilter = async (query, filters = {}) => {
    const customerIds = await resolveApprovalsPagedCustomerIds(filters);
    if (customerIds !== null) {
        if (customerIds.length === 0) return { query, empty: true };
        query = query.in('customer_id', customerIds);
    }
    return { query, empty: false };
};

/** Approvals "Submitted by" filter — work tables use submitted_by; surveys use updated_by (legacy). */
const applyApprovalsSubmittedByFilter = (query, filters = {}, { surveyUsesUpdatedBy = false } = {}) => {
    const submittedBy = filters.submitted_by;
    if (!submittedBy || submittedBy === 'all') return query;
    if (submittedBy === 'not_set') {
        return surveyUsesUpdatedBy
            ? query.is('updated_by', null)
            : query.is('submitted_by', null);
    }
    return surveyUsesUpdatedBy
        ? query.eq('updated_by', submittedBy)
        : query.eq('submitted_by', submittedBy);
};

const getCurrentUserId = () => {
    try {
        const storedUser = localStorage.getItem("currentUser");
        if (!storedUser) return null;
        const userData = JSON.parse(storedUser);
        return userData?.id || null;
    } catch {
        return null;
    }
};

const withUpdatedBy = (payload = {}) => {
    const currentUserId = getCurrentUserId();
    // Respect explicit updated_by if caller sets it
    return {
        ...payload,
        updated_by: payload?.updated_by ?? currentUserId
    };
};

/** Civil / transport / technical / commissioning — sets updated_by and resolves submitted_by on submit. */
const withWorkRecordPayload = (payload = {}, { isCreate = false } = {}) => {
    const currentUserId = getCurrentUserId();
    const result = {
        ...payload,
        updated_by: payload?.updated_by ?? currentUserId,
    };
    if (isCreate && currentUserId) {
        result.created_by = payload.created_by ?? currentUserId;
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'submitted_by')) {
        result.submitted_by = payload.submitted_by ?? currentUserId ?? null;
    }
    return result;
};

/** Backfill created_by on legacy rows the first time they are updated after deploy. */
const mergeWorkRecordCreatedBy = async (table, id, updates = {}) => {
    if (Object.prototype.hasOwnProperty.call(updates, 'created_by')) {
        return updates;
    }
    const currentUserId = getCurrentUserId();
    if (!currentUserId) return updates;

    const { data: row, error } = await supabase
        .from(table)
        .select('created_by')
        .eq('id', id)
        .maybeSingle();

    if (error) throw error;
    if (row?.created_by != null) return updates;

    return { ...updates, created_by: currentUserId };
};

const applyWorkRecordUpdate = async (table, columns, id, updates) => {
    const merged = await mergeWorkRecordCreatedBy(table, id, updates);
    const { data, error } = await supabase
        .from(table)
        .update(withWorkRecordPayload(merged))
        .eq('id', id)
        .select(columns)
        .single();

    if (error) throw error;
    return data;
};

const CIVIL_WORK_COLUMNS =
    'id, customer_id, photos, notes, approval_status, approval_decision_by, approval_decision_at, disapproval_reason, submitted_by, created_by, updated_by, created_at, updated_at';

const TRANSPORT_WORK_COLUMNS =
    'id, customer_id, customer_kit_photo, panel_structure_photo, notes, approval_status, approval_decision_by, approval_decision_at, disapproval_reason, submitted_by, created_by, updated_by, created_at, updated_at';

const TECHNICAL_WORK_COLUMNS =
    'id, customer_id, panels, wiring_inverter, wiring, earthing, testing, installation_photos, customer_photo, voltage_reading, current_reading, installation_completed_at, notes, approval_status, approval_decision_by, approval_decision_at, disapproval_reason, submitted_by, created_by, updated_by, created_at, updated_at';

const removeStoragePrefixRecursive = async (bucket, prefix) => {
    const normalizedPrefix = String(prefix || '').replace(/^\/+|\/+$/g, '');
    if (!normalizedPrefix) return;

    const collectFiles = async (folder = '') => {
        const { data, error } = await supabase.storage
            .from(bucket)
            .list(folder, { limit: 1000 });

        if (error) {
            console.warn(`Error listing storage path ${bucket}/${folder}:`, error);
            return [];
        }

        const entries = Array.isArray(data) ? data : [];
        const files = [];
        for (const entry of entries) {
            const entryName = entry?.name;
            if (!entryName) continue;
            const isDirectory = entry?.id == null;
            const childPath = folder ? `${folder}/${entryName}` : entryName;
            if (isDirectory) {
                const nestedFiles = await collectFiles(childPath);
                files.push(...nestedFiles);
            } else {
                files.push(childPath);
            }
        }
        return files;
    };

    const allFilePaths = await collectFiles(normalizedPrefix);
    if (!allFilePaths.length) return;

    const chunkSize = 100;
    for (let i = 0; i < allFilePaths.length; i += chunkSize) {
        const chunk = allFilePaths.slice(i, i + chunkSize);
        const { error } = await supabase.storage
            .from(bucket)
            .remove(chunk);
        if (error) {
            console.warn(`Error deleting storage files in ${bucket}/${normalizedPrefix}:`, error);
        }
    }
};

// =====================================================
// PROJECTS CRUD
// =====================================================

export const projects = {
    // Get all projects
    getAll: async () => {
        const { data, error } = await supabase
            .from('projects')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
    },

    // Get a single project by ID
    getById: async (id) => {
        const { data, error } = await supabase
            .from('projects')
            .select('*')
            .eq('id', id)
            .single();

        if (error) throw error;
        return data;
    },

    // Create a new project
    create: async (project) => {
        const { data, error } = await supabase
            .from('projects')
            .insert([project])
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    // Update a project
    update: async (id, updates) => {
        const { data, error } = await supabase
            .from('projects')
            .update(updates)
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    // Delete a project
    delete: async (id) => {
        const { error } = await supabase
            .from('projects')
            .delete()
            .eq('id', id);

        if (error) throw error;
    }
};

// =====================================================
// REGIONS CRUD (formerly sites)
// =====================================================

export const regions = {
    // Get all regions
    getAll: async () => {
        const { data, error } = await supabase
            .from('regions')
            .select('*, projects(name)')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
    },

    // Get regions by project ID
    getByProjectId: async (projectId) => {
        const { data, error } = await supabase
            .from('regions')
            .select('*, projects(name)')
            .eq('project_id', projectId)
            .order('name', { ascending: true });

        if (error) throw error;
        return data;
    },

    // Get a single region by ID
    getById: async (id) => {
        const { data, error } = await supabase
            .from('regions')
            .select('*, projects(name)')
            .eq('id', id)
            .single();

        if (error) throw error;
        return data;
    },

    // Create a new region
    create: async (region) => {
        const { data, error } = await supabase
            .from('regions')
            .insert([region])
            .select('*, projects(name)')
            .single();

        if (error) throw error;
        return data;
    },

    // Update a region
    update: async (id, updates) => {
        const { data, error } = await supabase
            .from('regions')
            .update(updates)
            .eq('id', id)
            .select('*, projects(name)')
            .single();

        if (error) throw error;
        return data;
    },

    // Delete a region
    delete: async (id) => {
        const { error } = await supabase
            .from('regions')
            .delete()
            .eq('id', id);

        if (error) throw error;
    }
};

// Keep sites as alias for backward compatibility (maps to regions)
export const sites = regions;

// =====================================================
// CLUSTERS CRUD
// =====================================================

export const clusters = {
    // Get all clusters
    getAll: async () => {
        const { data, error } = await supabase
            .from('clusters')
            .select('id, name, project_id, created_at, updated_at, projects(name)')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
    },

    // Get clusters by project ID
    getByProjectId: async (projectId) => {
        const { data, error } = await supabase
            .from('clusters')
            .select('id, name, project_id, created_at, updated_at, projects(name)')
            .eq('project_id', projectId)
            .order('name', { ascending: true });

        if (error) throw error;
        return data;
    },

    // Get a single cluster by ID
    getById: async (id) => {
        const { data, error } = await supabase
            .from('clusters')
            .select('id, name, project_id, created_at, updated_at, projects(name)')
            .eq('id', id)
            .single();

        if (error) throw error;
        return data;
    },

    // Create a new cluster
    create: async (cluster) => {
        const { data, error } = await supabase
            .from('clusters')
            .insert([cluster])
            .select('*, projects(name)')
            .single();

        if (error) throw error;
        return data;
    },

    // Update a cluster
    update: async (id, updates) => {
        const { data, error } = await supabase
            .from('clusters')
            .update(updates)
            .eq('id', id)
            .select('*, projects(name)')
            .single();

        if (error) throw error;
        return data;
    },

    // Delete a cluster
    delete: async (id) => {
        const { error } = await supabase
            .from('clusters')
            .delete()
            .eq('id', id);

        if (error) throw error;
    }
};


// =====================================================
// CUSTOMERS CRUD
// =====================================================

const getCompletionPhaseConfig = (phase) => {
    const normalized = String(phase || 'survey').trim().toLowerCase();
    if (normalized === 'transport') {
        return {
            joinSelect: 'transport_work!inner(updated_at)',
            timestampColumn: 'transport_work.updated_at'
        };
    }
    if (normalized === 'civil') {
        return {
            joinSelect: 'civil_work!inner(updated_at)',
            timestampColumn: 'civil_work.updated_at'
        };
    }
    if (normalized === 'technical') {
        return {
            joinSelect: 'technical_work!inner(installation_completed_at)',
            timestampColumn: 'technical_work.installation_completed_at'
        };
    }
    if (normalized === 'commissioning') {
        return {
            joinSelect: 'commissioning_work!inner(commissioning_completed_at)',
            timestampColumn: 'commissioning_work.commissioning_completed_at'
        };
    }
    return {
        joinSelect: 'surveys!inner(submitted_at)',
        timestampColumn: 'surveys.submitted_at'
    };
};

const applyCompletionPhaseFilter = (query, filters = {}) => {
    if (!filters.phase_completed_from && !filters.phase_completed_to) return query;
    const { timestampColumn } = getCompletionPhaseConfig(filters.completion_phase);
    if (filters.phase_completed_from) {
        query = query.filter(timestampColumn, 'gte', filters.phase_completed_from);
    }
    if (filters.phase_completed_to) {
        query = query.filter(timestampColumn, 'lte', filters.phase_completed_to);
    }
    return query;
};

const buildCustomerBaseSelect = (filters = {}, includeRelations = true) => {
    const hasCompletionFilter = filters.phase_completed_from || filters.phase_completed_to;
    const relationSelect = includeRelations
        ? 'projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)'
        : '';
    const selectParts = [];
    selectParts.push('*');
    if (relationSelect) selectParts.push(relationSelect);
    if (hasCompletionFilter) {
        selectParts.push(getCompletionPhaseConfig(filters.completion_phase).joinSelect);
    }
    return selectParts.join(', ');
};

export const customers = {
    // Get total count of customers (with optional filters)
    getCount: async (filters = {}) => {
        const baseSelect = buildCustomerBaseSelect(filters, false);
        let query = supabase
            .from('customers')
            .select(baseSelect, { count: 'exact', head: true });

        query = applyCompletionPhaseFilter(query, filters);
        if (filters.project_id) {
            query = query.eq('project_id', filters.project_id);
        }
        if (filters.cluster_id) {
            query = query.eq('cluster_id', filters.cluster_id);
        }
        if (filters.region_id?.length) {
            if (Array.isArray(filters.region_id)) {
                query = query.in('region_id', filters.region_id);
            } else {
                query = query.eq('region_id', filters.region_id);
            }
        }
        if (filters.team_id) {
            if (filters.team_id === "not_assigned") {
                query = query.is('team_id', null);
            } else {
                query = query.eq('team_id', filters.team_id);
            }
        }
        if (filters.status) {
            if (Array.isArray(filters.status)) {
                query = query.in('status', filters.status);
            } else {
                query = query.eq('status', filters.status);
            }
        }
        if (filters.search) {
            const searchTerm = filters.search;
            query = query.or(`name.ilike.%${searchTerm}%,phone_number.ilike.%${searchTerm}%,cin.ilike.%${searchTerm}%,usn.ilike.%${searchTerm}%,aadhaar_number.ilike.%${searchTerm}%`);
        }

        const { count, error } = await query;

        if (error) throw error;
        return count || 0;
    },

    // Get all customers with pagination support
    getAll: async (options = {}) => {
        const { page = 1, pageSize = 1000, filters = {} } = options;
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;

        const baseSelect = buildCustomerBaseSelect(filters, true);

        let query = supabase
            .from('customers')
            .select(baseSelect)
            .order('cin', { ascending: true })
            .range(from, to);

        query = applyCompletionPhaseFilter(query, filters);
        if (filters.project_id) {
            query = query.eq('project_id', filters.project_id);
        }
        if (filters.cluster_id) {
            query = query.eq('cluster_id', filters.cluster_id);
        }
        if (filters.region_id?.length) {
            if (Array.isArray(filters.region_id)) {
                query = query.in('region_id', filters.region_id);
            } else {
                query = query.eq('region_id', filters.region_id);
            }
        }
        if (filters.team_id) {
            if (filters.team_id === "not_assigned") {
                query = query.is('team_id', null);
            } else {
                query = query.eq('team_id', filters.team_id);
            }
        }
        if (filters.status) {
            if (Array.isArray(filters.status)) {
                query = query.in('status', filters.status);
            } else {
                query = query.eq('status', filters.status);
            }
        }
        if (filters.search) {
            const searchTerm = filters.search;
            query = query.or(`name.ilike.%${searchTerm}%,phone_number.ilike.%${searchTerm}%,cin.ilike.%${searchTerm}%,usn.ilike.%${searchTerm}%,aadhaar_number.ilike.%${searchTerm}%`);
        }

        const { data, error } = await query;

        if (error) throw error;
        return data || [];
    },

    // Get customers by project ID with pagination support
    getByProjectId: async (projectId, options = {}) => {
        const { page = 1, pageSize = 1000, filters = {} } = options;
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;

        const baseSelect = buildCustomerBaseSelect(filters, true);

        let query = supabase
            .from('customers')
            .select(baseSelect)
            .eq('project_id', projectId)
            .order('cin', { ascending: true })
            .range(from, to);

        query = applyCompletionPhaseFilter(query, filters);
        if (filters.cluster_id) {
            query = query.eq('cluster_id', filters.cluster_id);
        }
        if (filters.region_id?.length) {
            if (Array.isArray(filters.region_id)) {
                query = query.in('region_id', filters.region_id);
            } else {
                query = query.eq('region_id', filters.region_id);
            }
        }
        if (filters.team_id) {
            if (filters.team_id === "not_assigned") {
                query = query.is('team_id', null);
            } else {
                query = query.eq('team_id', filters.team_id);
            }
        }
        if (filters.status) {
            if (Array.isArray(filters.status)) {
                query = query.in('status', filters.status);
            } else {
                query = query.eq('status', filters.status);
            }
        }
        if (filters.search) {
            const searchTerm = filters.search;
            query = query.or(`name.ilike.%${searchTerm}%,phone_number.ilike.%${searchTerm}%,cin.ilike.%${searchTerm}%,usn.ilike.%${searchTerm}%,aadhaar_number.ilike.%${searchTerm}%`);
        }

        const { data, error } = await query;

        if (error) throw error;
        return data || [];
    },

    // Get customers by region ID
    getByRegionId: async (regionId) => {
        const { data, error } = await supabase
            .from('customers')
            .select('*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)')
            .eq('region_id', regionId)
            .order('cin', { ascending: true });

        if (error) throw error;
        return data;
    },

    // Get customers by cluster ID with pagination support
    getByClusterId: async (clusterId, options = {}) => {
        const { page = 1, pageSize = 1000, filters = {} } = options;
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;

        const baseSelect = buildCustomerBaseSelect(filters, true);

        let query = supabase
            .from('customers')
            .select(baseSelect)
            .eq('cluster_id', clusterId)
            .order('cin', { ascending: true })
            .range(from, to);

        query = applyCompletionPhaseFilter(query, filters);
        if (filters.region_id?.length) {
            if (Array.isArray(filters.region_id)) {
                query = query.in('region_id', filters.region_id);
            } else {
                query = query.eq('region_id', filters.region_id);
            }
        }
        if (filters.team_id) {
            if (filters.team_id === "not_assigned") {
                query = query.is('team_id', null);
            } else {
                query = query.eq('team_id', filters.team_id);
            }
        }
        if (filters.status) {
            if (Array.isArray(filters.status)) {
                query = query.in('status', filters.status);
            } else {
                query = query.eq('status', filters.status);
            }
        }
        if (filters.search) {
            const searchTerm = filters.search;
            query = query.or(`name.ilike.%${searchTerm}%,phone_number.ilike.%${searchTerm}%,cin.ilike.%${searchTerm}%,usn.ilike.%${searchTerm}%,aadhaar_number.ilike.%${searchTerm}%`);
        }

        const { data, error } = await query;

        if (error) throw error;
        return data || [];
    },

    // Get customers by team ID
    getByTeamId: async (teamId) => {
        const { data, error } = await supabase
            .from('customers')
            .select('*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)')
            .eq('team_id', teamId)
            .order('cin', { ascending: true });

        if (error) throw error;
        return data;
    },

    // Get customers assigned to a field user (by team_id)
    getByFieldUserId: async (userId) => {
        // First, get the user to check their team_id
        const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id, team_id, cluster_id')
            .eq('id', userId)
            .single();

        if (userError) throw userError;
        if (!userData || !userData.team_id) return [];

        // Get customers assigned to the user's team
        const { data, error } = await supabase
            .from('customers')
            .select('*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)')
            .eq('team_id', userData.team_id)
            .order('cin', { ascending: true });

        if (error) throw error;
        return data || [];
    },

    // Get customers assigned to a specific user by staff_assignments
    getByAssignedMemberId: async (memberId) => {
        // Get assignments for this member
        const { data: assignments, error: assignmentsError } = await supabase
            .from('staff_assignments')
            .select('customer_id')
            .eq('user_id', memberId);

        if (assignmentsError) throw assignmentsError;
        if (!assignments || assignments.length === 0) return [];

        // Get unique customer IDs from assignments
        const customerIds = [...new Set(assignments.map(a => a.customer_id))];

        // Fetch customers by IDs
        const { data, error } = await supabase
            .from('customers')
            .select('*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)')
            .in('id', customerIds)
            .order('cin', { ascending: true });

        if (error) throw error;
        return data || [];
    },

    // Get customers assigned to a user (by staff_assignments or team_id)
    getByUserId: async (userId) => {
        // First, get the user to check their team_id
        const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id, team_id, cluster_id')
            .eq('id', userId)
            .single();

        if (userError) throw userError;
        if (!userData) return [];

        // Get customer IDs from staff_assignments for this user
        const { data: assignments, error: assignmentsError } = await supabase
            .from('staff_assignments')
            .select('customer_id')
            .eq('user_id', userId);

        if (assignmentsError) throw assignmentsError;

        const assignedCustomerIds = assignments ? [...new Set(assignments.map(a => a.customer_id))] : [];

        // Build query: customers assigned to this user via staff_assignments OR by team_id
        // Fetch customers by assignments and by team separately, then combine and deduplicate

        let allCustomers = [];
        const customerIdsSet = new Set();

        // Fetch customers by assignments
        if (assignedCustomerIds.length > 0) {
            const { data: assignedCustomers, error: assignedError } = await supabase
                .from('customers')
                .select('*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)')
                .in('id', assignedCustomerIds);

            if (assignedError) throw assignedError;
            if (assignedCustomers) {
                assignedCustomers.forEach(c => {
                    if (!customerIdsSet.has(c.id)) {
                        customerIdsSet.add(c.id);
                        allCustomers.push(c);
                    }
                });
            }
        }

        // Fetch customers by team_id
        if (userData.team_id) {
            const { data: teamCustomers, error: teamError } = await supabase
                .from('customers')
                .select('*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)')
                .eq('team_id', userData.team_id);

            if (teamError) throw teamError;
            if (teamCustomers) {
                teamCustomers.forEach(c => {
                    if (!customerIdsSet.has(c.id)) {
                        customerIdsSet.add(c.id);
                        allCustomers.push(c);
                    }
                });
            }
        }

        // Sort by created_at descending
        allCustomers.sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return dateB - dateA;
        });

        return allCustomers;
    },

    // Get a single customer by ID
    getById: async (id) => {
        try {
            // First, query customer without nested foreign keys to ensure aadhaar_number is returned
            const { data: customerData, error: customerError } = await supabase
                .from('customers')
                .select('*')
                .eq('id', id)
                .maybeSingle();

            if (customerError) {
                console.error('Error fetching customer by ID:', customerError);
                throw customerError;
            }

            if (!customerData) {
                return null;
            }

            // Now fetch related data separately if customer exists
            const relatedDataPromises = [];

            if (customerData.project_id) {
                relatedDataPromises.push(
                    supabase.from('projects').select('name').eq('id', customerData.project_id).maybeSingle()
                );
            }
            if (customerData.region_id) {
                relatedDataPromises.push(
                    supabase.from('regions').select('name, project_id').eq('id', customerData.region_id).maybeSingle()
                );
            }
            if (customerData.cluster_id) {
                relatedDataPromises.push(
                    supabase.from('clusters').select('name, project_id').eq('id', customerData.cluster_id).maybeSingle()
                );
            }
            if (customerData.team_id) {
                relatedDataPromises.push(
                    supabase.from('teams').select('name, cluster_id').eq('id', customerData.team_id).maybeSingle()
                );
            }

            const relatedResults = await Promise.all(relatedDataPromises);

            // Attach related data to customer object
            let index = 0;
            if (customerData.project_id) {
                customerData.projects = relatedResults[index]?.data || null;
                index++;
            }
            if (customerData.region_id) {
                customerData.regions = relatedResults[index]?.data || null;
                index++;
            }
            if (customerData.cluster_id) {
                customerData.clusters = relatedResults[index]?.data || null;
                index++;
            }
            if (customerData.team_id) {
                customerData.teams = relatedResults[index]?.data || null;
                index++;
            }


            return customerData;
        } catch (err) {
            console.error('Unexpected error in getById:', err);
            throw err;
        }
    },

    // Get customer by CIN (Customer Identification Number - auto-generated)
    getByCIN: async (cin) => {
        const { data, error } = await supabase
            .from('customers')
            .select('*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)')
            .eq('cin', cin)
            .single();

        if (error) throw error;
        return data;
    },

    // Get customer by USN (User Service Number - from Excel/govt)
    getByUSN: async (usn) => {
        const { data, error } = await supabase
            .from('customers')
            .select('*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)')
            .eq('usn', usn)
            .single();

        if (error) throw error;
        return data;
    },

    // Get customer by USN if exists (returns null when not found) - for upsert on upload
    getByUsnMaybe: async (usn) => {
        const trimmed = usn != null ? String(usn).trim() : '';
        if (!trimmed) return null;
        const { data, error } = await supabase
            .from('customers')
            .select('id, usn, project_id, region_id, cluster_id')
            .eq('usn', trimmed)
            .maybeSingle();

        if (error) throw error;
        return data;
    },

    // Get customers by multiple CINs or USNs (bulk search)
    // options: { projectId?, clusterId? } to restrict to a project (super_admin/project_admin) or cluster (cluster_manager)
    getByMultipleCINsOrUSNs: async (identifierList, options = {}) => {
        if (!identifierList || identifierList.length === 0) return [];

        let query = supabase
            .from('customers')
            .select('*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)')
            .or(`cin.in.(${identifierList.join(',')}),usn.in.(${identifierList.join(',')})`)
            .order('cin', { ascending: true });

        if (options.projectId) {
            query = query.eq('project_id', options.projectId);
        }
        if (options.clusterId) {
            query = query.eq('cluster_id', options.clusterId);
        }

        const { data, error } = await query;
        if (error) throw error;
        return data || [];
    },

    // Search customers
    search: async (searchTerm) => {
        const { data, error } = await supabase
            .from('customers')
            .select('*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)')
            .or(`name.ilike.%${searchTerm}%,phone_number.ilike.%${searchTerm}%,cin.ilike.%${searchTerm}%,usn.ilike.%${searchTerm}%,aadhaar_number.ilike.%${searchTerm}%`)
            .order('cin', { ascending: true });

        if (error) throw error;
        return data;
    },

    // Create a new customer
    create: async (customer) => {
        const { data, error } = await supabase
            .from('customers')
            .insert([withUpdatedBy(customer)])
            .select('*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)')
            .single();

        if (error) throw error;
        return data;
    },

    // Update a customer
    update: async (id, updates) => {
        const { data, error } = await supabase
            .from('customers')
            .update(withUpdatedBy(updates))
            .eq('id', id)
            .select('*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)')
            .maybeSingle();

        if (error) throw error;
        return data;
    },

    // Delete a customer
    delete: async (id) => {
        // 1) Delete related survey (if any) and its storage objects (survey-photos bucket)
        try {
            const survey = await surveys.getByCustomerId(id);
            if (survey?.id) {
                try {
                    await removeStoragePrefixRecursive('survey-photos', survey.id);
                } catch (storageErr) {
                    console.error('Unexpected error while deleting survey storage files for customer', id, storageErr);
                }

                try {
                    await surveys.delete(survey.id);
                } catch (surveyErr) {
                    console.error('Error deleting survey for customer', id, surveyErr);
                    throw surveyErr;
                }
            }
        } catch (surveyFetchErr) {
            console.error('Error fetching survey for customer before delete', id, surveyFetchErr);
            throw surveyFetchErr;
        }

        // 2) Delete installation photos for this customer (civil/technical/commissioning)
        try {
            await removeStoragePrefixRecursive('installation-photos', id);
        } catch (installStorageErr) {
            console.error('Unexpected error while deleting installation storage files for customer', id, installStorageErr);
        }

        // 3) Delete related work/assignment records tied to this customer
        try {
            // Transport, civil, technical, and commissioning work
            const { error: transportError } = await supabase
                .from('transport_work')
                .delete()
                .eq('customer_id', id);
            if (transportError) {
                console.warn('Error deleting transport_work for customer', id, transportError);
            }

            const { error: civilError } = await supabase
                .from('civil_work')
                .delete()
                .eq('customer_id', id);
            if (civilError) {
                console.warn('Error deleting civil_work for customer', id, civilError);
            }

            const { error: techError } = await supabase
                .from('technical_work')
                .delete()
                .eq('customer_id', id);
            if (techError) {
                console.warn('Error deleting technical_work for customer', id, techError);
            }

            const { error: commissioningError } = await supabase
                .from('commissioning_work')
                .delete()
                .eq('customer_id', id);
            if (commissioningError) {
                console.warn('Error deleting commissioning_work for customer', id, commissioningError);
            }

            // Staff assignments and completions
            try {
                await staffAssignments.deleteByCustomer(id);
            } catch (assignErr) {
                console.warn('Error deleting staff assignments for customer', id, assignErr);
            }

            try {
                await staffCompletions.deleteByCustomer(id);
            } catch (completionErr) {
                console.warn('Error deleting staff completions for customer', id, completionErr);
            }

            // Notifications that reference this customer (best-effort cleanup)
            try {
                const { error: notifError } = await supabase
                    .from('notifications')
                    .delete()
                    .eq('customer_id', id);
                if (notifError) {
                    console.warn('Error deleting notifications for customer', id, notifError);
                }
            } catch (notifErr) {
                console.warn('Unexpected error while deleting notifications for customer', id, notifErr);
            }
        } catch (relatedErr) {
            console.error('Unexpected error while deleting related records for customer', id, relatedErr);
            throw relatedErr;
        }

        // 4) Finally delete the customer record itself
        const { error: customerError } = await supabase
            .from('customers')
            .delete()
            .eq('id', id);

        if (customerError) throw customerError;
    }
};

// =====================================================
// USERS CRUD (replaces profiles)
// =====================================================

export const users = {
    // Get all users (without passwords)
    getAll: async () => {
        const { data, error } = await supabase
            .from('users')
            .select('id, unique_id, role, category, name, project_id, cluster_id, team_id, created_at, updated_at, projects(name), clusters(name, project_id), teams(name, cluster_id)')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
    },

    // Get user by unique_id
    getByUniqueId: async (uniqueId) => {
        const { data, error } = await supabase
            .from('users')
            .select('id, unique_id, role, category, name, project_id, cluster_id, team_id, created_at, updated_at, projects(name), clusters(name, project_id), teams(name, cluster_id)')
            .eq('unique_id', uniqueId.toUpperCase().trim())
            .single();

        if (error) throw error;
        return data;
    },

    // Get users by project ID
    getByProjectId: async (projectId) => {
        const { data, error } = await supabase
            .from('users')
            .select('id, unique_id, role, category, name, project_id, cluster_id, team_id, created_at, updated_at, projects(name), clusters(name, project_id), teams(name, cluster_id)')
            .eq('project_id', projectId)
            .order('name', { ascending: true });

        if (error) throw error;
        return data;
    },

    // Get users by cluster ID
    getByClusterId: async (clusterId) => {
        const { data, error } = await supabase
            .from('users')
            .select('id, unique_id, role, category, name, project_id, cluster_id, team_id, created_at, updated_at, projects(name), clusters(name, project_id), teams(name, cluster_id)')
            .eq('cluster_id', clusterId)
            .order('name', { ascending: true });

        if (error) throw error;
        return data;
    },

    // Get users by team ID
    getByTeamId: async (teamId) => {
        const { data, error } = await supabase
            .from('users')
            .select('id, unique_id, role, category, name, project_id, cluster_id, team_id, created_at, updated_at, projects(name), clusters(name, project_id), teams(name, cluster_id)')
            .eq('team_id', teamId)
            .order('name', { ascending: true });

        if (error) throw error;
        return data;
    },

    // Get users by role
    getByRole: async (role) => {
        const { data, error } = await supabase
            .from('users')
            .select('id, unique_id, role, category, name, project_id, cluster_id, team_id, created_at, updated_at, projects(name), clusters(name, project_id), teams(name, cluster_id)')
            .eq('role', role)
            .order('name', { ascending: true });

        if (error) throw error;
        return data;
    },

    // Get users by category (admin/staff)
    getByCategory: async (category) => {
        const { data, error } = await supabase
            .from('users')
            .select('id, unique_id, role, category, name, project_id, cluster_id, team_id, created_at, updated_at, projects(name), clusters(name, project_id), teams(name, cluster_id)')
            .eq('category', category)
            .order('name', { ascending: true });

        if (error) throw error;
        return data;
    },


    // Get current user (from localStorage)
    getCurrent: async () => {
        const storedUser = localStorage.getItem("currentUser");
        if (!storedUser) throw new Error('User not authenticated');

        const userData = JSON.parse(storedUser);
        const { data, error } = await supabase
            .from('users')
            .select('id, unique_id, role, category, name, project_id, cluster_id, team_id, created_at, updated_at, projects(name), clusters(name, project_id), teams(name, cluster_id)')
            .eq('id', userData.id)
            .single();

        if (error) throw error;
        return data;
    },

    // Get a single user by ID (without password)
    getById: async (id) => {
        const { data, error } = await supabase
            .from('users')
            .select('id, unique_id, role, category, name, project_id, cluster_id, team_id, created_at, updated_at, projects(name), clusters(name, project_id), teams(name, cluster_id)')
            .eq('id', id)
            .single();

        if (error) throw error;
        return data;
    },

    // Get multiple users by ID (without password)
    getByIds: async (ids) => {
        const uniqueIds = [...new Set((ids || []).filter(Boolean))];
        if (uniqueIds.length === 0) return [];

        const { data, error } = await supabase
            .from('users')
            .select('id, unique_id, role, category, name, project_id, cluster_id, team_id, created_at, updated_at, projects(name), clusters(name, project_id), teams(name, cluster_id)')
            .in('id', uniqueIds);

        if (error) throw error;
        return data || [];
    },

    // Get user password by ID
    getPasswordById: async (id) => {
        const { data, error } = await supabase
            .from('users')
            .select('password')
            .eq('id', id)
            .single();

        if (error) throw error;
        return data?.password || null;
    },

    // Create a new user
    create: async (userData) => {
        const { data, error } = await supabase
            .from('users')
            .insert([userData])
            .select('id, unique_id, role, category, name, project_id, cluster_id, team_id, created_at, updated_at')
            .single();

        if (error) throw error;
        return data;
    },

    // Update a user
    update: async (id, updates) => {
        const { data, error } = await supabase
            .from('users')
            .update(updates)
            .eq('id', id)
            .select('id, unique_id, role, category, name, project_id, cluster_id, team_id, created_at, updated_at, projects(name), clusters(name, project_id), teams(name, cluster_id)')
            .single();

        if (error) throw error;
        return data;
    },

    // Delete a user
    delete: async (id) => {
        const { error } = await supabase
            .from('users')
            .delete()
            .eq('id', id);

        if (error) throw error;
    }
};

// Keep profiles as alias for backward compatibility
export const profiles = users;

// =====================================================
// TEAMS & TEAM MEMBERS CRUD
// =====================================================

export const teams = {
    // Get all teams with cluster relation
    getAll: async () => {
        const { data, error } = await supabase
            .from('teams')
            .select(`
                *,
                clusters:cluster_id (name, project_id, projects(name))
            `)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
    },

    // Get teams by project ID (through clusters)
    getByProjectId: async (projectId) => {
        // First get all clusters for this project
        const { data: projectClusters, error: clustersError } = await supabase
            .from('clusters')
            .select('id')
            .eq('project_id', projectId);

        if (clustersError) throw clustersError;

        if (!projectClusters || projectClusters.length === 0) {
            return [];
        }

        const clusterIds = projectClusters.map(c => c.id);

        const { data, error } = await supabase
            .from('teams')
            .select(`
                *,
                clusters:cluster_id (name, project_id, projects(name))
            `)
            .in('cluster_id', clusterIds)
            .order('name', { ascending: true });

        if (error) throw error;
        return data || [];
    },

    // Get teams by cluster ID
    getByClusterId: async (clusterId) => {
        const { data, error } = await supabase
            .from('teams')
            .select(`
                *,
                clusters:cluster_id (name, project_id, projects(name))
            `)
            .eq('cluster_id', clusterId)
            .order('name', { ascending: true });

        if (error) throw error;
        return data;
    },

    // Get a single team by ID
    getById: async (id) => {
        const { data, error } = await supabase
            .from('teams')
            .select(`
                *,
                clusters:cluster_id (name, project_id, projects(name))
            `)
            .eq('id', id)
            .single();

        if (error) throw error;
        return data;
    },

    // Create a new team
    create: async (team) => {
        const { data, error } = await supabase
            .from('teams')
            .insert([team])
            .select('*')
            .single();

        if (error) throw error;
        return data;
    },

    // Update an existing team
    update: async (id, updates) => {
        const { data, error } = await supabase
            .from('teams')
            .update(updates)
            .eq('id', id)
            .select('*')
            .single();

        if (error) throw error;
        return data;
    },

    // Delete a team
    delete: async (id) => {
        const { error } = await supabase
            .from('teams')
            .delete()
            .eq('id', id);

        if (error) throw error;
    }
};

// teamMembers API removed - team_id is now stored directly in users table

// =====================================================
// SURVEYS CRUD
// =====================================================

export const surveys = {
    // Get all surveys
    getAll: async () => {
        const { data, error } = await supabase
            .from('surveys')
            .select(`
        *,
        customers(*, clusters(name, project_id, projects(name)))
      `)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
    },

    // Get surveys for approvals queue (completed surveys and beyond)
    // Filters:
    // - project_id: filter by customers.project_id
    // - cluster_id: filter by customers.cluster_id
    // - approval_status: filter by surveys.approval_status
    getForApprovals: async (filters = {}) => {
        const buildQuery = () => {
            let query = supabase
                .from('surveys')
                .select(`
        *,
        customers!inner(*, clusters(name, project_id, projects(name)), teams(name))
      `)
                .order('created_at', { ascending: false });

            if (filters.project_id) {
                query = query.eq('customers.project_id', filters.project_id);
            }

            if (filters.cluster_id) {
                query = query.eq('customers.cluster_id', filters.cluster_id);
            }

            if (filters.approval_status) {
                query = query.eq('approval_status', filters.approval_status);
            }

            return query;
        };

        // Use paging to avoid server-enforced default max rows (often 100)
        return await fetchAllPages(buildQuery, { pageSize: 1000 });
    },

    // Paged version for approvals screen (lazy loading)
    // Supports extra filters used by Approvals UI:
    // - region_id: array or single
    // - team_id: "not_assigned" | specific team id
    // - search: matches customers.name or customers.cin
    // - customer_ids: restrict to specific customers (e.g., by staff_assignments)
    // - submitted_from/submitted_to: filter surveys.submitted_at
    // Returns: { data: [], count: number }
    getForApprovalsPaged: async (filters = {}, options = {}) => {
        const { from, to } = getPageRange(options.page, options.pageSize);

        let query = supabase
            .from('surveys')
            .select(`
        *,
        customers!inner(*, clusters(name, project_id, projects(name)), teams(name))
      `, { count: 'exact' })
            .order('created_at', { ascending: false });

        if (filters.project_id) query = query.eq('customers.project_id', filters.project_id);
        if (filters.cluster_id) query = query.eq('customers.cluster_id', filters.cluster_id);
        if (filters.approval_status) query = query.eq('approval_status', filters.approval_status);

        if (filters.region_id?.length) {
            query = Array.isArray(filters.region_id)
                ? query.in('customers.region_id', filters.region_id)
                : query.eq('customers.region_id', filters.region_id);
        }

        if (filters.team_id) {
            if (filters.team_id === 'not_assigned') query = query.is('customers.team_id', null);
            else query = query.eq('customers.team_id', filters.team_id);
        }

        const customerFilter = await applyApprovalsPagedCustomerFilter(query, filters);
        if (customerFilter.empty) return { data: [], count: 0 };
        query = customerFilter.query;

        query = applyApprovalsSubmittedByFilter(query, filters, { surveyUsesUpdatedBy: true });

        if (filters.submitted_from) query = query.gte('submitted_at', filters.submitted_from);
        if (filters.submitted_to) query = query.lte('submitted_at', filters.submitted_to);

        const { data, count, error } = await query.range(from, to);
        if (error) throw error;
        return { data: data || [], count: count || 0 };
    },

    // Minimal surveys dataset for dashboard aggregations
    // Filters:
    // - project_id: customers.project_id
    // - cluster_id: customers.cluster_id
    // Returns: [{ id, customer_id, roof_feasible, submitted_at, customers: { id, village, project_id, cluster_id } }]
    getDashboardMinimal: async (filters = {}) => {
        const buildQuery = () => {
            let query = supabase
                .from('surveys')
                .select(`
          id,
          customer_id,
          roof_feasible,
          submitted_at,
          customers!inner(id, village, project_id, cluster_id)
        `)
                .order('created_at', { ascending: false });

            if (filters.project_id) query = query.eq('customers.project_id', filters.project_id);
            if (filters.cluster_id) query = query.eq('customers.cluster_id', filters.cluster_id);

            return query;
        };

        return await fetchAllPages(buildQuery, { pageSize: 1000 });
    },

    // Get surveys for customers with status survey_unworkable (Unworkable page)
    // Filters: project_id, cluster_id (same scope as approvals)
    getUnworkable: async (filters = {}) => {
        let query = supabase
            .from('surveys')
            .select(`
        *,
        customers!inner(*, clusters(name, project_id, projects(name)), teams(name))
      `)
            .eq('customers.status', 'survey_unworkable')
            .order('created_at', { ascending: false });

        if (filters.project_id) {
            query = query.eq('customers.project_id', filters.project_id);
        }

        if (filters.cluster_id) {
            query = query.eq('customers.cluster_id', filters.cluster_id);
        }

        const { data, error } = await query;
        if (error) throw error;
        return data || [];
    },

    // Get approval_status for multiple customers (for gating civil status assignment)
    getApprovalStatusByCustomerIds: async (customerIds) => {
        if (!customerIds || customerIds.length === 0) return [];
        const { data, error } = await supabase
            .from('surveys')
            .select('customer_id, approval_status')
            .in('customer_id', customerIds);
        if (error) throw error;
        return data || [];
    },

    // Get survey by customer ID
    getByCustomerId: async (customerId) => {
        const { data, error } = await supabase
            .from('surveys')
            .select(`
        *,
        customers(*, clusters(name, project_id, projects(name)))
      `)
            .eq('customer_id', customerId)
            .maybeSingle(); // Use maybeSingle() instead of single() to return null if no rows

        // PGRST116 means no rows found, which is fine - return null
        if (error && error.code !== 'PGRST116') {
            throw error;
        }
        return data || null;
    },

    // Get a single survey by ID
    getById: async (id) => {
        const { data, error } = await supabase
            .from('surveys')
            .select(`
        *,
        customers(*, clusters(name, project_id, projects(name)))
      `)
            .eq('id', id)
            .single();

        if (error) throw error;
        return data;
    },

    // Get surveys by cluster ID (through customers)
    getByClusterId: async (clusterId) => {
        const { data, error } = await supabase
            .from('surveys')
            .select(`
        *,
        customers!inner(*, clusters!inner(name, project_id, projects(name)))
      `)
            .eq('customers.cluster_id', clusterId)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
    },

    // Create a new survey
    create: async (survey) => {
        const { data, error } = await supabase
            .from('surveys')
            .insert([withUpdatedBy(survey)])
            .select(`
        *,
        customers(*, clusters(name, project_id, projects(name)))
      `)
            .single();

        if (error) throw error;
        return data;
    },

    // Upsert a survey
    upsert: async (survey) => {
        // For Supabase, upsert automatically uses unique constraints/primary keys
        // The surveys table has a UNIQUE constraint on customer_id, so upsert will work correctly
        const { data, error } = await supabase
            .from('surveys')
            .upsert([withUpdatedBy(survey)], {
                onConflict: 'customer_id' // Use the unique constraint column
            })
            .select(`
        *,
        customers(*, clusters(name, project_id, projects(name)))
      `)
            .single();

        if (error) throw error;
        return data;
    },

    // Update a survey
    update: async (id, updates) => {
        const { data, error } = await supabase
            .from('surveys')
            .update(withUpdatedBy(updates))
            .eq('id', id)
            .select(`
        *,
        customers(*, clusters(name, project_id, projects(name)))
      `)
            .single();

        if (error) throw error;
        return data;
    },

    // Delete a survey
    delete: async (id) => {
        const { error } = await supabase
            .from('surveys')
            .delete()
            .eq('id', id);

        if (error) throw error;
    },

    // Upload QR code image (stored in survey-photos bucket)
    // options.skipSurveyUpdate: if true, only upload and return URL (caller batches update)
    uploadQRCode: async (surveyId, file, options = {}) => {
        let fileToUpload = file;
        if (file?.type?.startsWith?.('image/')) {
            try {
                fileToUpload = await compressImage(file, { preset: 'fast' });
            } catch (e) {
                console.warn('Image compression failed, uploading original:', e);
            }
        }
        const fileExt = fileToUpload.name.split('.').pop();
        const fileName = `qr-${surveyId}-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${surveyId}/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('survey-photos')
            .upload(filePath, fileToUpload, {
                cacheControl: '3600',
                upsert: false
            });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
            .from('survey-photos')
            .getPublicUrl(filePath);

        if (!options.skipSurveyUpdate) {
            await surveys.update(surveyId, { qr_code_photo: publicUrl });
        }
        return publicUrl;
    },

    // Upload categorized photos (roof_inspection, roof, meter, line_diagram, obstacle)
    uploadCategoryPhotos: async (surveyId, files, category) => {
        const categoryPrefix = {
            roof_inspection: 'roof-inspection',
            roof: 'roof',
            meter: 'meter',
            line_diagram: 'line',
            obstacle: 'obstacle'
        }[category] || 'photo';

        const uploadPromises = files.map(async (file) => {
            let fileToUpload = file;
            if (file?.type?.startsWith?.('image/')) {
                try {
                    fileToUpload = await compressImage(file, { preset: 'fast' });
                } catch (e) {
                    console.warn('Image compression failed, uploading original:', e);
                }
            }
            const fileExt = fileToUpload.name.split('.').pop();
            const fileName = `${categoryPrefix}-${surveyId}-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
            const filePath = `${surveyId}/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('survey-photos')
                .upload(filePath, fileToUpload);

            if (uploadError) throw uploadError;

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('survey-photos')
                .getPublicUrl(filePath);

            return publicUrl;
        });

        return await Promise.all(uploadPromises);
    },

    // Upload single survey assets (signatures, tentative location photos, etc.)
    // assetKey should match the target column name in surveys table.
    // options.skipSurveyUpdate: if true, only upload and return URL (caller batches update)
    uploadSurveyAsset: async (surveyId, file, assetKey, options = {}) => {
        let fileToUpload = file;
        if (file?.type?.startsWith?.('image/')) {
            try {
                fileToUpload = await compressImage(file, { preset: 'fast' });
            } catch (e) {
                console.warn('Image compression failed, uploading original:', e);
            }
        }
        const fileExt = fileToUpload.name.split('.').pop();
        const safeKey = (assetKey || 'asset').replace(/[^a-z0-9_]/gi, '_').toLowerCase();
        const fileName = `${safeKey}-${surveyId}-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${surveyId}/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('survey-photos')
            .upload(filePath, fileToUpload, {
                cacheControl: '3600',
                upsert: false
            });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
            .from('survey-photos')
            .getPublicUrl(filePath);

        if (!options.skipSurveyUpdate) {
            await surveys.update(surveyId, { [assetKey]: publicUrl });
        }
        return publicUrl;
    },

    // Upload document photos (aadhar, pan, bank_passbook, electricity_bill)
    // When VITE_DOCUMENT_ENCRYPTION_KEY is set, files are encrypted (AES-GCM) before upload.
    uploadDocumentPhoto: async (surveyId, file, documentType) => {
        let fileToUpload = file;
        if (file?.type?.startsWith?.('image/')) {
            try {
                fileToUpload = await compressImage(file, { preset: 'document' });
            } catch (e) {
                console.warn('Image compression failed, uploading original:', e);
            }
        }

        let encrypted = false;
        const encryptEnabled = await isEncryptionEnabled();
        if (encryptEnabled) {
            const encryptedBlob = await encryptFile(fileToUpload);
            if (encryptedBlob) {
                fileToUpload = encryptedBlob;
                encrypted = true;
            }
        }

        const fileExt = encrypted ? 'enc' : (fileToUpload.name?.split('.').pop() || 'jpg');
        const fileName = `${documentType}-${surveyId}-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${surveyId}/${fileName}`;

        const uploadOptions = {
            cacheControl: '3600',
            upsert: false,
            ...(encrypted && { contentType: 'application/octet-stream' })
        };

        const { error: uploadError } = await supabase.storage
            .from('survey-photos')
            .upload(filePath, fileToUpload, uploadOptions);

        if (uploadError) throw uploadError;

        // Get public URL (caller batches survey update)
        const { data: { publicUrl } } = supabase.storage
            .from('survey-photos')
            .getPublicUrl(filePath);

        return publicUrl;
    }
};

// =====================================================
// SURVEY REPORT TOKENS (public links for PDF - no backend required)
// =====================================================

export const surveyReportTokens = {
    /**
     * Create tokens for multiple customers. Used when exporting to Excel.
     * Returns { [customerId]: token }.
     */
    createForCustomers: async (customerIds) => {
        if (!customerIds?.length) return {};
        const rows = customerIds.map((id) => ({
            token: crypto.randomUUID(),
            customer_id: id,
        }));
        const { error } = await supabase
            .from('survey_report_tokens')
            .insert(rows);
        if (error) {
            console.warn('survey_report_tokens create error:', error);
            return {};
        }
        const map = {};
        rows.forEach((r) => {
            map[r.customer_id] = r.token;
        });
        return map;
    },

    /**
     * Get customer + survey by token (for public survey report page). Callable by anon.
     * Returns { data, error }. data is { customer, survey } when successful.
     */
    getReportByToken: async (token) => {
        const raw = typeof token === 'string' ? token.trim() : '';
        if (!raw) return { data: null, error: new Error('Missing token') };
        const { data: rawData, error } = await supabase.rpc('get_survey_report_by_token', {
            p_token: raw,
        });
        if (error) return { data: null, error };
        if (rawData == null) return { data: null, error: new Error('Token not found or no survey') };
        const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
        return { data, error: null };
    },
};

// =====================================================
// TECHNICAL REPORT TOKENS (public links for technical view - no auth)
// =====================================================

export const technicalReportTokens = {
    /**
     * Create tokens for multiple customers. Used when exporting to Excel.
     * Returns { [customerId]: token }.
     */
    createForCustomers: async (customerIds) => {
        if (!customerIds?.length) return {};
        const rows = customerIds.map((id) => ({
            token: crypto.randomUUID(),
            customer_id: id,
        }));
        const { error } = await supabase
            .from('technical_report_tokens')
            .insert(rows);
        if (error) {
            console.warn('technical_report_tokens create error:', error);
            return {};
        }
        const map = {};
        rows.forEach((r) => {
            map[r.customer_id] = r.token;
        });
        return map;
    },

    /**
     * Get customer + technical_work by token (for public technical report page). Callable by anon.
     * Returns { data, error }. data is { customer, technicalWork } when successful.
     */
    getReportByToken: async (token) => {
        const raw = typeof token === 'string' ? token.trim() : '';
        if (!raw) return { data: null, error: new Error('Missing token') };
        const { data: rawData, error } = await supabase.rpc('get_technical_report_by_token', {
            p_token: raw,
        });
        if (error) return { data: null, error };
        if (rawData == null) return { data: null, error: new Error('Token not found or no technical work') };
        const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
        return { data, error: null };
    },
};

// =====================================================
// WORK COMPLETION REPORT TOKENS (public links for WCR PDF - no auth)
// =====================================================

export const workCompletionReportTokens = {
    /**
     * Create tokens for multiple customers. Used when exporting to Excel.
     * Returns { [customerId]: token }.
     */
    createForCustomers: async (customerIds) => {
        if (!customerIds?.length) return {};
        const rows = customerIds.map((id) => ({
            token: crypto.randomUUID(),
            customer_id: id,
        }));
        const { error } = await supabase
            .from('work_completion_report_tokens')
            .insert(rows);
        if (error) {
            console.warn('work_completion_report_tokens create error:', error);
            return {};
        }
        const map = {};
        rows.forEach((r) => {
            map[r.customer_id] = r.token;
        });
        return map;
    },

    /**
     * Get all data needed to generate a Work Completion Report PDF by token. Callable by anon.
     * Returns { data, error }. data shape is expected:
     * { customer, survey, civilWork, technicalWork, commissioningWork, region, cluster, users }
     */
    getReportByToken: async (token) => {
        const raw = typeof token === 'string' ? token.trim() : '';
        if (!raw) return { data: null, error: new Error('Missing token') };
        const { data: rawData, error } = await supabase.rpc('get_work_completion_report_by_token', {
            p_token: raw,
        });
        if (error) return { data: null, error };
        if (rawData == null) return { data: null, error: new Error('Token not found or no data') };
        const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
        return { data, error: null };
    },
};

// =====================================================
// CIVIL WORK (INSTALLATION WORKFLOW - CIVIL PHASE) CRUD
// =====================================================

export const civilWork = {
    // Get civil work record by customer ID
    getByCustomerId: async (customerId) => {
        const { data, error } = await supabase
            .from('civil_work')
            .select(CIVIL_WORK_COLUMNS)
            .eq('customer_id', customerId)
            .maybeSingle();

        if (error) throw error;
        return data || null;
    },

    // Get civil work items for approvals queue
    // Filters:
    // - project_id: filter by customers.project_id
    // - cluster_id: filter by customers.cluster_id
    // - approval_status: filter by civil_work.approval_status
    getForApprovals: async (filters = {}) => {
        const buildQuery = () => {
            let query = supabase
                .from('civil_work')
                .select(`
        ${CIVIL_WORK_COLUMNS},
        customers!inner(*, clusters(name, project_id, projects(name)), teams(name))
      `)
                .order('updated_at', { ascending: false });

            if (filters.project_id) {
                query = query.eq('customers.project_id', filters.project_id);
            }

            if (filters.cluster_id) {
                query = query.eq('customers.cluster_id', filters.cluster_id);
            }

            if (filters.approval_status) {
                query = query.eq('approval_status', filters.approval_status);
            }

            return query;
        };

        return await fetchAllPages(buildQuery, { pageSize: 1000 });
    },

    // Paged version for approvals screen (lazy loading)
    // Returns: { data: [], count: number }
    getForApprovalsPaged: async (filters = {}, options = {}) => {
        const { from, to } = getPageRange(options.page, options.pageSize);

        let query = supabase
            .from('civil_work')
            .select(`
        ${CIVIL_WORK_COLUMNS},
        customers!inner(*, clusters(name, project_id, projects(name)), teams(name))
      `, { count: 'exact' })
            .order('updated_at', { ascending: false });

        if (filters.project_id) query = query.eq('customers.project_id', filters.project_id);
        if (filters.cluster_id) query = query.eq('customers.cluster_id', filters.cluster_id);
        if (filters.approval_status) query = query.eq('approval_status', filters.approval_status);

        if (filters.region_id?.length) {
            query = Array.isArray(filters.region_id)
                ? query.in('customers.region_id', filters.region_id)
                : query.eq('customers.region_id', filters.region_id);
        }

        if (filters.team_id) {
            if (filters.team_id === 'not_assigned') query = query.is('customers.team_id', null);
            else query = query.eq('customers.team_id', filters.team_id);
        }

        const customerFilter = await applyApprovalsPagedCustomerFilter(query, filters);
        if (customerFilter.empty) return { data: [], count: 0 };
        query = customerFilter.query;

        query = applyApprovalsSubmittedByFilter(query, filters);

        // Approvals “civil complete” date filter (datetime-local range → updated_at proxy)
        if (filters.phase_completed_from) query = query.gte('updated_at', filters.phase_completed_from);
        if (filters.phase_completed_to) query = query.lte('updated_at', filters.phase_completed_to);

        const { data, count, error } = await query.range(from, to);
        if (error) throw error;
        return { data: data || [], count: count || 0 };
    },

    // Get approval_status for multiple customers (for gating technical status assignment)
    getApprovalStatusByCustomerIds: async (customerIds) => {
        if (!customerIds || customerIds.length === 0) return [];
        const { data, error } = await supabase
            .from('civil_work')
            .select('customer_id, approval_status')
            .in('customer_id', customerIds);
        if (error) throw error;
        return data || [];
    },

    getById: async (id) => {
        const { data, error } = await supabase
            .from('civil_work')
            .select(`
        ${CIVIL_WORK_COLUMNS},
        customers(*, clusters(name, project_id, projects(name)), teams(name))
      `)
            .eq('id', id)
            .maybeSingle();

        if (error) throw error;
        return data || null;
    },

    // Create a new civil work record
    create: async (payload) => {
        const { data, error } = await supabase
            .from('civil_work')
            .insert([withWorkRecordPayload(payload, { isCreate: true })])
            .select(CIVIL_WORK_COLUMNS)
            .single();

        if (error) throw error;
        return data;
    },

    // Update an existing civil work record by ID
    update: async (id, updates) => {
        return applyWorkRecordUpdate('civil_work', CIVIL_WORK_COLUMNS, id, updates);
    },

    // Upsert by customer (create if not exists, else update)
    upsertForCustomer: async (customerId, payload) => {
        // Try to get existing record
        const existing = await civilWork.getByCustomerId(customerId);

        if (existing) {
            return await civilWork.update(existing.id, payload);
        }

        return await civilWork.create({
            ...payload,
            customer_id: customerId
        });
    },

    // Upload civil work photo
    uploadPhoto: async (customerId, file, photoId) => {
        let fileToUpload = file;
        if (file?.type?.startsWith?.('image/')) {
            try {
                fileToUpload = await compressImage(file);
            } catch (e) {
                console.warn('Image compression failed, uploading original:', e);
            }
        }
        const fileExt = fileToUpload.name.split('.').pop();
        const fileName = `civil-${customerId}-${photoId}-${Date.now()}.${fileExt}`;
        const filePath = `${customerId}/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('installation-photos')
            .upload(filePath, fileToUpload);

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
            .from('installation-photos')
            .getPublicUrl(filePath);

        return publicUrl;
    }
};

// =====================================================
// TECHNICAL WORK (INSTALLATION WORKFLOW - TECHNICAL PHASE) CRUD
// =====================================================

export const technicalWork = {
    // Get technical work record by customer ID
    getByCustomerId: async (customerId) => {
        const { data, error } = await supabase
            .from('technical_work')
            .select(TECHNICAL_WORK_COLUMNS)
            .eq('customer_id', customerId)
            .maybeSingle();

        if (error) throw error;
        return data || null;
    },

    // Get approval_status for multiple customers (gating commissioning assignment)
    getApprovalStatusByCustomerIds: async (customerIds) => {
        if (!customerIds || customerIds.length === 0) return [];
        const { data, error } = await supabase
            .from('technical_work')
            .select('customer_id, approval_status')
            .in('customer_id', customerIds);
        if (error) throw error;
        return data || [];
    },

    getById: async (id) => {
        const { data, error } = await supabase
            .from('technical_work')
            .select(`
        ${TECHNICAL_WORK_COLUMNS},
        customers(*, clusters(name, project_id, projects(name)), teams(name))
      `)
            .eq('id', id)
            .maybeSingle();

        if (error) throw error;
        return data || null;
    },

    // Get technical work items for approvals queue
    // Filters:
    // - project_id: filter by customers.project_id
    // - cluster_id: filter by customers.cluster_id
    // - approval_status: filter by technical_work.approval_status
    getForApprovals: async (filters = {}) => {
        const buildQuery = () => {
            let query = supabase
                .from('technical_work')
                .select(`
        ${TECHNICAL_WORK_COLUMNS},
        customers!inner(*, clusters(name, project_id, projects(name)), teams(name))
      `)
                .order('updated_at', { ascending: false });

            if (filters.project_id) {
                query = query.eq('customers.project_id', filters.project_id);
            }

            if (filters.cluster_id) {
                query = query.eq('customers.cluster_id', filters.cluster_id);
            }

            if (filters.approval_status) {
                query = query.eq('approval_status', filters.approval_status);
            }

            return query;
        };

        return await fetchAllPages(buildQuery, { pageSize: 1000 });
    },

    // Paged version for approvals screen (lazy loading)
    // Returns: { data: [], count: number }
    getForApprovalsPaged: async (filters = {}, options = {}) => {
        const { from, to } = getPageRange(options.page, options.pageSize);

        let query = supabase
            .from('technical_work')
            .select(`
        ${TECHNICAL_WORK_COLUMNS},
        customers!inner(*, clusters(name, project_id, projects(name)), teams(name))
      `, { count: 'exact' })
            .order('updated_at', { ascending: false });

        if (filters.project_id) query = query.eq('customers.project_id', filters.project_id);
        if (filters.cluster_id) query = query.eq('customers.cluster_id', filters.cluster_id);
        if (filters.approval_status) query = query.eq('approval_status', filters.approval_status);

        if (filters.region_id?.length) {
            query = Array.isArray(filters.region_id)
                ? query.in('customers.region_id', filters.region_id)
                : query.eq('customers.region_id', filters.region_id);
        }

        if (filters.team_id) {
            if (filters.team_id === 'not_assigned') query = query.is('customers.team_id', null);
            else query = query.eq('customers.team_id', filters.team_id);
        }

        const customerFilter = await applyApprovalsPagedCustomerFilter(query, filters);
        if (customerFilter.empty) return { data: [], count: 0 };
        query = customerFilter.query;

        query = applyApprovalsSubmittedByFilter(query, filters);

        if (filters.phase_completed_from) query = query.gte('installation_completed_at', filters.phase_completed_from);
        if (filters.phase_completed_to) query = query.lte('installation_completed_at', filters.phase_completed_to);

        const { data, count, error } = await query.range(from, to);
        if (error) throw error;
        return { data: data || [], count: count || 0 };
    },

    // Create a new technical work record
    create: async (payload) => {
        const { data, error } = await supabase
            .from('technical_work')
            .insert([withWorkRecordPayload(payload, { isCreate: true })])
            .select(TECHNICAL_WORK_COLUMNS)
            .single();

        if (error) throw error;
        return data;
    },

    // Update an existing technical work record by ID
    update: async (id, updates) => {
        return applyWorkRecordUpdate(
            'technical_work',
            TECHNICAL_WORK_COLUMNS,
            id,
            updates,
        );
    },

    // Upsert by customer (create if not exists, else update)
    upsertForCustomer: async (customerId, payload) => {
        const existing = await technicalWork.getByCustomerId(customerId);

        if (existing) {
            return await technicalWork.update(existing.id, payload);
        }

        return await technicalWork.create({
            ...payload,
            customer_id: customerId
        });
    },

    // Upload technical work photo
    uploadPhoto: async (customerId, file, photoId) => {
        let fileToUpload = file;
        if (file?.type?.startsWith?.('image/')) {
            try {
                fileToUpload = await compressImage(file);
            } catch (e) {
                console.warn('Image compression failed, uploading original:', e);
            }
        }
        const fileExt = fileToUpload.name.split('.').pop();
        const fileName = `technical-${customerId}-${photoId}-${Date.now()}.${fileExt}`;
        const filePath = `${customerId}/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('installation-photos')
            .upload(filePath, fileToUpload);

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
            .from('installation-photos')
            .getPublicUrl(filePath);

        return publicUrl;
    }
};

// =====================================================
// COMMISSIONING WORK (POST-TECHNICAL PHASE) CRUD
// =====================================================

export const commissioningWork = {
    getByCustomerId: async (customerId) => {
        const { data, error } = await supabase
            .from('commissioning_work')
            .select('*')
            .eq('customer_id', customerId)
            .maybeSingle();

        if (error) throw error;
        return data || null;
    },

    getById: async (id) => {
        const { data, error } = await supabase
            .from('commissioning_work')
            .select(`
        *,
        customers(*, clusters(name, project_id, projects(name)), teams(name))
      `)
            .eq('id', id)
            .maybeSingle();

        if (error) throw error;
        return data || null;
    },

    getForApprovalsPaged: async (filters = {}, options = {}) => {
        const { from, to } = getPageRange(options.page, options.pageSize);

        let query = supabase
            .from('commissioning_work')
            .select(`
        *,
        customers!inner(*, clusters(name, project_id, projects(name)), teams(name))
      `, { count: 'exact' })
            .order('updated_at', { ascending: false });

        if (filters.project_id) query = query.eq('customers.project_id', filters.project_id);
        if (filters.cluster_id) query = query.eq('customers.cluster_id', filters.cluster_id);
        if (filters.approval_status) query = query.eq('approval_status', filters.approval_status);

        if (filters.region_id?.length) {
            query = Array.isArray(filters.region_id)
                ? query.in('customers.region_id', filters.region_id)
                : query.eq('customers.region_id', filters.region_id);
        }

        if (filters.team_id) {
            if (filters.team_id === 'not_assigned') query = query.is('customers.team_id', null);
            else query = query.eq('customers.team_id', filters.team_id);
        }

        const customerFilter = await applyApprovalsPagedCustomerFilter(query, filters);
        if (customerFilter.empty) return { data: [], count: 0 };
        query = customerFilter.query;

        query = applyApprovalsSubmittedByFilter(query, filters);

        if (filters.phase_completed_from) query = query.gte('commissioning_completed_at', filters.phase_completed_from);
        if (filters.phase_completed_to) query = query.lte('commissioning_completed_at', filters.phase_completed_to);

        const { data, count, error } = await query.range(from, to);
        if (error) throw error;
        return { data: data || [], count: count || 0 };
    },

    create: async (payload) => {
        const { data, error } = await supabase
            .from('commissioning_work')
            .insert([withWorkRecordPayload(payload, { isCreate: true })])
            .select('*')
            .single();

        if (error) throw error;
        return data;
    },

    update: async (id, updates) => {
        return applyWorkRecordUpdate('commissioning_work', '*', id, updates);
    },

    upsertForCustomer: async (customerId, payload) => {
        const existing = await commissioningWork.getByCustomerId(customerId);

        if (existing) {
            return await commissioningWork.update(existing.id, payload);
        }

        return await commissioningWork.create({
            ...payload,
            customer_id: customerId
        });
    },

    uploadPhoto: async (customerId, file, photoId) => {
        let fileToUpload = file;
        if (file?.type?.startsWith?.('image/')) {
            try {
                fileToUpload = await compressImage(file);
            } catch (e) {
                console.warn('Image compression failed, uploading original:', e);
            }
        }
        const fileExt = fileToUpload.name.split('.').pop();
        const fileName = `commissioning-${customerId}-${photoId}-${Date.now()}.${fileExt}`;
        const filePath = `${customerId}/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('installation-photos')
            .upload(filePath, fileToUpload);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
            .from('installation-photos')
            .getPublicUrl(filePath);

        return publicUrl;
    }
};

// =====================================================
// TRANSPORT WORK (KIT DELIVERY — BEFORE CIVIL PHASE) CRUD
// =====================================================

export const transportWork = {
    getByCustomerId: async (customerId) => {
        const { data, error } = await supabase
            .from('transport_work')
            .select(TRANSPORT_WORK_COLUMNS)
            .eq('customer_id', customerId)
            .maybeSingle();

        if (error) throw error;
        return data || null;
    },

    getById: async (id) => {
        const { data, error } = await supabase
            .from('transport_work')
            .select(`
        ${TRANSPORT_WORK_COLUMNS},
        customers(*, clusters(name, project_id, projects(name)), teams(name))
      `)
            .eq('id', id)
            .maybeSingle();

        if (error) throw error;
        return data || null;
    },

    getForApprovalsPaged: async (filters = {}, options = {}) => {
        const { from, to } = getPageRange(options.page, options.pageSize);

        let query = supabase
            .from('transport_work')
            .select(`
        ${TRANSPORT_WORK_COLUMNS},
        customers!inner(*, clusters(name, project_id, projects(name)), teams(name))
      `, { count: 'exact' })
            .order('updated_at', { ascending: false });

        if (filters.project_id) query = query.eq('customers.project_id', filters.project_id);
        if (filters.cluster_id) query = query.eq('customers.cluster_id', filters.cluster_id);
        if (filters.approval_status) query = query.eq('approval_status', filters.approval_status);

        if (filters.region_id?.length) {
            query = Array.isArray(filters.region_id)
                ? query.in('customers.region_id', filters.region_id)
                : query.eq('customers.region_id', filters.region_id);
        }

        if (filters.team_id) {
            if (filters.team_id === 'not_assigned') query = query.is('customers.team_id', null);
            else query = query.eq('customers.team_id', filters.team_id);
        }

        const customerFilter = await applyApprovalsPagedCustomerFilter(query, filters);
        if (customerFilter.empty) return { data: [], count: 0 };
        query = customerFilter.query;

        query = applyApprovalsSubmittedByFilter(query, filters);

        // Approvals “transport complete” date filter (datetime-local range → updated_at proxy)
        if (filters.phase_completed_from) query = query.gte('updated_at', filters.phase_completed_from);
        if (filters.phase_completed_to) query = query.lte('updated_at', filters.phase_completed_to);

        const { data, count, error } = await query.range(from, to);
        if (error) throw error;
        return { data: data || [], count: count || 0 };
    },

    getApprovalStatusByCustomerIds: async (customerIds) => {
        if (!customerIds || customerIds.length === 0) return [];
        const { data, error } = await supabase
            .from('transport_work')
            .select('customer_id, approval_status')
            .in('customer_id', customerIds);
        if (error) throw error;
        return data || [];
    },

    create: async (payload) => {
        const { data, error } = await supabase
            .from('transport_work')
            .insert([withWorkRecordPayload(payload, { isCreate: true })])
            .select(TRANSPORT_WORK_COLUMNS)
            .single();

        if (error) throw error;
        return data;
    },

    update: async (id, updates) => {
        return applyWorkRecordUpdate(
            'transport_work',
            TRANSPORT_WORK_COLUMNS,
            id,
            updates,
        );
    },

    upsertForCustomer: async (customerId, payload) => {
        const existing = await transportWork.getByCustomerId(customerId);

        if (existing) {
            return await transportWork.update(existing.id, payload);
        }

        return await transportWork.create({
            ...payload,
            customer_id: customerId
        });
    },

    uploadPhoto: async (customerId, file, photoId) => {
        let fileToUpload = file;
        if (file?.type?.startsWith?.('image/')) {
            try {
                fileToUpload = await compressImage(file);
            } catch (e) {
                console.warn('Image compression failed, uploading original:', e);
            }
        }
        const fileExt = fileToUpload.name.split('.').pop();
        const fileName = `transport-${customerId}-${photoId}-${Date.now()}.${fileExt}`;
        const filePath = `${customerId}/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('installation-photos')
            .upload(filePath, fileToUpload);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
            .from('installation-photos')
            .getPublicUrl(filePath);

        return publicUrl;
    }
};

// =====================================================
// STAFF COMPLETIONS CRUD (TRACK COMPLETED WORK PER STAFF MEMBER)
// =====================================================

export const staffCompletions = {
    // Get all completions for a staff member
    getByStaffMemberId: async (staffMemberId, completionType = null) => {
        let query = supabase
            .from('staff_completions')
            .select('*, customers(*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id))')
            .eq('staff_member_id', staffMemberId)
            .order('completed_at', { ascending: false });

        if (completionType) {
            query = query.eq('completion_type', completionType);
        }

        const { data, error } = await query;

        if (error) throw error;
        return data || [];
    },

    // Get completion for a specific customer and staff member
    getByCustomerAndStaff: async (customerId, staffMemberId, completionType = null) => {
        let query = supabase
            .from('staff_completions')
            .select('*')
            .eq('customer_id', customerId)
            .eq('staff_member_id', staffMemberId);

        if (completionType) {
            query = query.eq('completion_type', completionType);
        }

        const { data, error } = await query.maybeSingle();

        if (error) throw error;
        return data || null;
    },

    // Check if a customer was completed by a staff member
    isCompletedByStaff: async (customerId, staffMemberId, completionType) => {
        const completion = await staffCompletions.getByCustomerAndStaff(customerId, staffMemberId, completionType);
        return !!completion;
    },

    // Create a completion record
    create: async (customerId, staffMemberId, completionType) => {
        const { data, error } = await supabase
            .from('staff_completions')
            .insert([{
                customer_id: customerId,
                staff_member_id: staffMemberId,
                completion_type: completionType,
                completed_at: new Date().toISOString()
            }])
            .select('*')
            .single();

        if (error) throw error;
        return data;
    },

    // Delete a completion record (used when reassigning)
    delete: async (customerId, staffMemberId, completionType = null) => {
        let query = supabase
            .from('staff_completions')
            .delete()
            .eq('customer_id', customerId)
            .eq('staff_member_id', staffMemberId);

        if (completionType) {
            query = query.eq('completion_type', completionType);
        }

        const { error } = await query;

        if (error) throw error;
    },

    // Delete all completions for a customer (used when reassigning to different staff)
    deleteByCustomer: async (customerId, completionType = null) => {
        let query = supabase
            .from('staff_completions')
            .delete()
            .eq('customer_id', customerId);

        if (completionType) {
            query = query.eq('completion_type', completionType);
        }

        const { error } = await query;

        if (error) throw error;
    }
};

// =====================================================
// STAFF ASSIGNMENTS CRUD (TRACK ALL ASSIGNMENTS AND THEIR STATUS)
// =====================================================

export const staffAssignments = {
    // Get all assignments for a staff member (by user_id)
    getByUserId: async (userId, status = null) => {
        let query = supabase
            .from('staff_assignments')
            .select('*, customers(*, projects(name), regions(name, project_id), clusters(name, project_id), teams(name, cluster_id)), users(id, name, unique_id)')
            .eq('user_id', userId);

        if (status) {
            query = query.eq('status', status);
        }

        query = query.order('assigned_at', { ascending: false });

        const { data, error } = await query;

        if (error) throw error;
        return data || [];
    },

    // Count assignments for a user where the linked customer is in specific statuses.
    // Useful for enforcing per-role assignment caps.
    getCustomerCountByUserIdAndCustomerStatuses: async (userId, customerStatuses = []) => {
        let query = supabase
            .from('staff_assignments')
            .select('id, customers!inner(id, status)', { count: 'exact', head: true })
            .eq('user_id', userId);

        if (Array.isArray(customerStatuses) && customerStatuses.length > 0) {
            query = query.in('customers.status', customerStatuses);
        }

        const { count, error } = await query;
        if (error) throw error;
        return count || 0;
    },

    // Get all assignments for a customer (or multiple customers)
    getByCustomerId: async (customerIdOrIds) => {
        // Handle single ID
        if (!Array.isArray(customerIdOrIds)) {
            const { data, error } = await supabase
                .from('staff_assignments')
                .select('*, users(id, name, unique_id, role)')
                .eq('customer_id', customerIdOrIds)
                .order('assigned_at', { ascending: false });

            if (error) throw error;
            return data || [];
        }

        // Handle array of IDs
        if (customerIdOrIds.length === 0) return [];

        // Batch large arrays to avoid URL length limits
        // Supabase PostgREST has URL length limits, so we batch requests
        const BATCH_SIZE = 100;
        const batches = [];

        for (let i = 0; i < customerIdOrIds.length; i += BATCH_SIZE) {
            batches.push(customerIdOrIds.slice(i, i + BATCH_SIZE));
        }

        // Execute all batches in parallel
        const batchPromises = batches.map(async (batch) => {
            const { data, error } = await supabase
                .from('staff_assignments')
                .select('*, users(id, name, unique_id, role)')
                .in('customer_id', batch)
                .order('assigned_at', { ascending: false });

            if (error) throw error;
            return data || [];
        });

        // Wait for all batches and combine results
        const batchResults = await Promise.all(batchPromises);
        const allAssignments = batchResults.flat();

        // Sort by assigned_at descending (most recent first)
        return allAssignments.sort((a, b) => {
            const dateA = new Date(a.assigned_at || 0);
            const dateB = new Date(b.assigned_at || 0);
            return dateB - dateA;
        });
    },

    // Lightweight helper for approvals filtering (Team Member = specific user)
    // Returns customer_id list for non-completed assignments.
    getActiveCustomerIdsByUserId: async (userId) => {
        if (!userId) return [];
        const { data, error } = await supabase
            .from('staff_assignments')
            .select('customer_id')
            .eq('user_id', userId)
            .neq('status', 'completed');
        if (error) throw error;
        return (data || []).map((r) => r.customer_id).filter(Boolean);
    },

    // Minimal assignments dataset for dashboard aggregations
    // Filters:
    // - project_id: customers.project_id
    // - user_role: users.role (e.g. "surveyor" | "civil" | "technical")
    // Returns: [{ id, customer_id, user_id, status, assigned_at, completed_at, customers: { id, village, project_id }, users: { id, role, team_id, name } }]
    getDashboardMinimal: async (filters = {}) => {
        const buildQuery = () => {
            let query = supabase
                .from('staff_assignments')
                .select(`
          id,
          customer_id,
          user_id,
          status,
          assigned_at,
          completed_at,
          customers!inner(id, village, project_id, cluster_id),
          users(id, role, team_id, name)
        `)
                .order('assigned_at', { ascending: false });

            if (filters.project_id) query = query.eq('customers.project_id', filters.project_id);
            if (filters.cluster_id) query = query.eq('customers.cluster_id', filters.cluster_id);
            if (filters.user_role) query = query.eq('users.role', filters.user_role);

            return query;
        };

        return await fetchAllPages(buildQuery, { pageSize: 1000 });
    },

    // Get a specific assignment
    getByCustomerAndUser: async (customerId, userId) => {
        const { data, error } = await supabase
            .from('staff_assignments')
            .select('*')
            .eq('customer_id', customerId)
            .eq('user_id', userId)
            .maybeSingle();

        if (error) throw error;
        return data || null;
    },

    // Create or update an assignment (upsert based on unique constraint)
    upsert: async (customerId, userId, updates = {}) => {
        const { data, error } = await supabase
            .from('staff_assignments')
            .upsert([{
                customer_id: customerId,
                user_id: userId,
                status: 'assigned',
                assigned_at: new Date().toISOString(),
                ...updates
            }], {
                onConflict: 'customer_id,user_id'
            })
            .select('*')
            .single();

        if (error) throw error;
        return data;
    },

    // Create a new assignment
    create: async (customerId, userId, dueDate = null) => {
        const { data, error } = await supabase
            .from('staff_assignments')
            .insert([{
                customer_id: customerId,
                user_id: userId,
                status: 'assigned',
                assigned_at: new Date().toISOString(),
                due_date: dueDate
            }])
            .select('*')
            .single();

        if (error) throw error;
        return data;
    },

    // Update assignment status
    updateStatus: async (customerId, userId, status, dueDate = null) => {
        const updateData = {
            status: status,
            updated_at: new Date().toISOString()
        };

        if (dueDate !== null) {
            updateData.due_date = dueDate;
        }

        const { data, error } = await supabase
            .from('staff_assignments')
            .update(updateData)
            .eq('customer_id', customerId)
            .eq('user_id', userId)
            .select('*')
            .single();

        if (error) throw error;
        return data;
    },

    // Update an assignment
    update: async (id, updates) => {
        const { data, error } = await supabase
            .from('staff_assignments')
            .update({
                ...updates,
                updated_at: new Date().toISOString()
            })
            .eq('id', id)
            .select('*')
            .single();

        if (error) throw error;
        return data;
    },

    // Delete an assignment
    delete: async (customerId, userId) => {
        const { error } = await supabase
            .from('staff_assignments')
            .delete()
            .eq('customer_id', customerId)
            .eq('user_id', userId);

        if (error) throw error;
    },

    // Delete all assignments for a customer (when reassigning)
    deleteByCustomer: async (customerId) => {
        const { error } = await supabase
            .from('staff_assignments')
            .delete()
            .eq('customer_id', customerId);

        if (error) throw error;
    },

    // Mark assignment as completed
    markCompleted: async (customerId, userId) => {
        return await staffAssignments.updateStatus(customerId, userId, 'completed');
    },

    // Mark assignment as overdue (based on due_date)
    markOverdue: async (customerId, userId) => {
        return await staffAssignments.updateStatus(customerId, userId, 'overdue');
    }
};

// Helper function to get cluster manager for a customer and assign them
export const assignToClusterManager = async (customerId) => {
    try {
        // Get customer with cluster_id
        const customer = await customers.getById(customerId);
        if (!customer || !customer.cluster_id) {
            console.warn(`Customer ${customerId} has no cluster_id, skipping cluster manager assignment`);
            return null;
        }

        // Get all users in the cluster
        const clusterUsers = await users.getByClusterId(customer.cluster_id);

        // Find cluster manager(s)
        const clusterManagers = clusterUsers.filter(
            (user) => user.role === 'cluster_manager'
        );

        if (clusterManagers.length === 0) {
            console.warn(`No cluster manager found for cluster ${customer.cluster_id}`);
            return null;
        }

        // Assign to all cluster managers (in case there are multiple)
        const assignments = await Promise.all(
            clusterManagers.map(async (manager) => {
                try {
                    // Check if assignment already exists
                    const existing = await staffAssignments.getByCustomerAndUser(
                        customerId,
                        manager.id
                    );

                    if (!existing) {
                        // Create new assignment
                        await staffAssignments.create(customerId, manager.id);
                        return manager.id;
                    }
                    return null;
                } catch (err) {
                    console.error(`Error assigning to cluster manager ${manager.id}:`, err);
                    return null;
                }
            })
        );

        return assignments.filter(Boolean);
    } catch (error) {
        console.error('Error assigning to cluster manager:', error);
        return null;
    }
};

// =====================================================
// NOTIFICATIONS CRUD
// =====================================================

export const notifications = {
    // Get all notifications for a user
    getByUserId: async (userId, options = {}) => {
        let query = supabase
            .from('notifications')
            .select('*, customers(id, name, cin, usn)')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

        // Filter by read status if provided
        if (options.read !== undefined) {
            query = query.eq('read', options.read);
        }

        // Filter by type if provided
        if (options.type) {
            query = query.eq('type', options.type);
        }

        // Limit results if provided
        if (options.limit) {
            query = query.limit(options.limit);
        }

        const { data, error } = await query;

        if (error) throw error;
        return data || [];
    },

    // Get unread count for a user
    getUnreadCount: async (userId) => {
        const { count, error } = await supabase
            .from('notifications')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            .eq('read', false);

        if (error) throw error;
        return count || 0;
    },

    // Get a single notification by ID
    getById: async (id) => {
        const { data, error } = await supabase
            .from('notifications')
            .select('*, customers(id, name, cin, usn)')
            .eq('id', id)
            .single();

        if (error) throw error;
        return data;
    },

    // Create a new notification
    create: async (notification) => {
        const { data, error } = await supabase
            .from('notifications')
            .insert([notification])
            .select('*, customers(id, name, cin, usn)')
            .single();

        if (error) throw error;
        return data;
    },

    // Create notifications for multiple users (bulk create)
    createForUsers: async (userIds, notificationData) => {
        const notifications = userIds.map(userId => ({
            ...notificationData,
            user_id: userId
        }));

        const { data, error } = await supabase
            .from('notifications')
            .insert(notifications)
            .select('*, customers(id, name, cin, usn)');

        if (error) throw error;
        return data || [];
    },

    // Mark notification as read
    markAsRead: async (id) => {
        const { data, error } = await supabase
            .from('notifications')
            .update({ read: true })
            .eq('id', id)
            .select('*, customers(id, name, cin, usn)')
            .single();

        if (error) throw error;
        return data;
    },

    // Mark all notifications as read for a user
    markAllAsRead: async (userId) => {
        const { data, error } = await supabase
            .from('notifications')
            .update({ read: true })
            .eq('user_id', userId)
            .eq('read', false)
            .select('*, customers(id, name, cin, usn)');

        if (error) throw error;
        return data || [];
    },

    // Delete a notification
    delete: async (id) => {
        const { error } = await supabase
            .from('notifications')
            .delete()
            .eq('id', id);

        if (error) throw error;
    },

    // Delete all notifications for a user
    deleteByUserId: async (userId) => {
        const { error } = await supabase
            .from('notifications')
            .delete()
            .eq('user_id', userId);

        if (error) throw error;
    },

    // Helper: Create notification for cluster manager when work is completed
    notifyClusterManagerOnWorkCompletion: async (customerId, workType) => {
        try {
            // Get customer with cluster_id
            const customer = await customers.getById(customerId);
            if (!customer || !customer.cluster_id) {
                console.warn(`Customer ${customerId} has no cluster_id, skipping cluster manager notification`);
                return null;
            }

            // Get all users in the cluster
            const clusterUsers = await users.getByClusterId(customer.cluster_id);

            // Find cluster manager(s)
            const clusterManagers = clusterUsers.filter(
                (user) => user.role === 'cluster_manager'
            );

            if (clusterManagers.length === 0) {
                console.warn(`No cluster manager found for cluster ${customer.cluster_id}`);
                return null;
            }

            // Create notification data based on work type
            const workTypeLabels = {
                'survey': 'Survey',
                'transport': 'Transport',
                'civil': 'Civil Work',
                'technical': 'Technical Work',
                'commissioning': 'Commissioning'
            };

            const workTypeLabel = workTypeLabels[workType] || workType;

            // Format customer info with CIN
            const customerInfo = customer.cin
                ? `${customer.name || 'Unknown'} (${customer.cin})`
                : customer.name || 'Unknown';

            const notificationData = {
                type: 'work_completed',
                title: `${workTypeLabel} Completed`,
                message: `${workTypeLabel} has been completed for customer ${customerInfo}`,
                priority: 'high',
                customer_id: customerId,
                metadata: {
                    work_type: workType,
                    customer_name: customer.name,
                    customer_cin: customer.cin,
                    customer_usn: customer.usn
                }
            };

            // Create notifications for all cluster managers
            const notificationIds = clusterManagers.map(manager => manager.id);
            return await notifications.createForUsers(notificationIds, notificationData);
        } catch (error) {
            console.error('Error creating cluster manager notification:', error);
            return null;
        }
    },

    // Helper: Create notification when customer is assigned to a team member
    notifyUserOnAssignment: async (userId, customerId) => {
        try {
            const customer = await customers.getById(customerId);
            if (!customer) {
                console.warn(`Customer ${customerId} not found, skipping assignment notification`);
                return null;
            }

            // Format customer info with CIN
            const customerInfo = customer.cin
                ? `${customer.name || 'Unknown'} (${customer.cin})`
                : customer.name || 'Unknown';

            const notificationData = {
                user_id: userId,
                type: 'assignment',
                title: 'New Work Assignment',
                message: `You have been assigned to customer ${customerInfo}`,
                priority: 'high',
                customer_id: customerId,
                metadata: {
                    customer_name: customer.name,
                    customer_cin: customer.cin,
                    customer_usn: customer.usn
                }
            };

            return await notifications.create(notificationData);
        } catch (error) {
            console.error('Error creating assignment notification:', error);
            return null;
        }
    }
};

// =====================================================
// APP SETTINGS
// =====================================================

export const appSettings = {
    get: async (settingKey, fallbackValue = null) => {
        const { data, error } = await supabase
            .from('app_settings')
            .select('*')
            .eq('setting_key', settingKey)
            .maybeSingle();

        if (error) throw error;
        return data?.value ?? fallbackValue;
    },

    set: async (settingKey, value) => {
        const currentUserId = getCurrentUserId();
        const { data, error } = await supabase
            .from('app_settings')
            .upsert(
                [{
                    setting_key: settingKey,
                    value,
                    updated_by: currentUserId
                }],
                { onConflict: 'setting_key' }
            )
            .select('*')
            .single();

        if (error) throw error;
        return data;
    },

    getGeolocationEnforcement: async () => {
        const value = await appSettings.get('geolocation_enforcement', { enabled: true });
        return value?.enabled !== false;
    },

    setGeolocationEnforcement: async (enabled) => {
        return await appSettings.set('geolocation_enforcement', {
            enabled: Boolean(enabled)
        });
    }
};
